import parse from 'html-react-parser';
import React from 'react';
import EmbedContainer from '@/components/EmbedContainer';
import AutoC2CFrame from '@/components/Embeds/AutoC2CFrame';
import Datawrapper from '@/components/Embeds/Datawrapper';
import Facebook from '@/components/Embeds/Facebook';
import Flourish from '@/components/Embeds/Flourish';
import GoogleMaps from '@/components/Embeds/GoogleMaps';
import Houdini from '@/components/Embeds/Houdini';
import Imgur from '@/components/Embeds/Imgur';
import Instagram from '@/components/Embeds/Instagram';
import MagnifiedGraphics from '@/components/Embeds/MagnifiedGraphics';
import OnnVideo from '@/components/Embeds/OnnVideo';
import OuterGifImage from '@/components/Embeds/OuterGifImage';
import Spotify from '@/components/Embeds/Spotify';
import Telegram from '@/components/Embeds/Telegram';
import TikTok from '@/components/Embeds/TikTok';
import Twitter from '@/components/Embeds/Twitter';
import UomArticle from '@/components/Embeds/UomArticle';
import UomImage from '@/components/Embeds/UomImage';
import Windy from '@/components/Embeds/Windy';
import Youtube from '@/components/Embeds/Youtube';
import { type INoteContent, type INoteItemExternalLink, type INoteItemText, type INoteItemUomImage, type TEmbedArticles } from '@/types/INote';

export default function NoteContent ({
  content,
  embedArticles,
  noteId,
}: {
  content: INoteContent;
  embedArticles?: TEmbedArticles;
  noteId: string;
}) {
  const paragraphContent = (item: INoteItemExternalLink | INoteItemText | INoteItemUomImage, idx: number) => {
    switch (item.type) {
      case 'externalLink':
        return <a href={item.url} key={`note-externalLink-${idx}`} rel={item.rel || ''}>{item.text}</a>;
      case 'text':
        return <React.Fragment key={`note-text-${idx}`}>{parse(item.text)}</React.Fragment>;
      default:
        return null;
    }
  };

  return (
    <>
      {content.elements.map((element, idxC) => {
        switch (element.type) {
          case 'blockquote':
            return (
              <blockquote data-e='note-blockquote' key={`note-blockquote-${noteId}`}>
                <div data-e='note-blockquote-icon'>
                  <svg height='12.485' width='14' xmlns='http://www.w3.org/2000/svg'>
                    <path d='M.971 12.486c3.468-.647 5.179-2.821 5.179-6.8V0H0v6.243h3.328a3.835 3.835 0 0 1-3 3.977zm7.85 0C12.289 11.839 14 9.665 14 5.686V0H7.85v6.243h3.329a3.835 3.835 0 0 1-3.006 3.977z' fill='currentcolor' />
                  </svg>
                </div>
                {parseToHTML(element.items)}
              </blockquote>
            );

          case 'embed':
          case 'uomEmbed':
            return element.items?.map((item, idx) => {
              switch (item.type) {
                case 'autoC2CFrame':
                  return (
                    <EmbedContainer isNoConsentNeeded key={`note-autoC2CFrame-${item.id}`}>
                      <AutoC2CFrame />
                    </EmbedContainer>
                  );

                case 'datawrapper':
                  return (
                    <EmbedContainer key={`note-datawrapper-${item.id}`}>
                      <Datawrapper {...item} />
                    </EmbedContainer>
                  );

                case 'facebook':
                  return (
                    <EmbedContainer key={`note-facebook-${item.id}`}>
                      <Facebook {...item} />
                    </EmbedContainer>
                  );

                case 'flourish':
                  return (
                    <EmbedContainer key={`note-flourish-${item.src}`}>
                      <Flourish {...item} />
                    </EmbedContainer>
                  );

                case 'googleMaps':
                  return (
                    <EmbedContainer key={`note-googleMaps-${item.id}`}>
                      <GoogleMaps {...item} />
                    </EmbedContainer>
                  );

                case 'houdini':
                  return (
                    <EmbedContainer key={`note-houdini-${item.id}`}>
                      <Houdini {...item} />
                    </EmbedContainer>
                  );

                case 'imgur':
                  return (
                    <EmbedContainer key={`note-imgur-${item.id}`}>
                      <Imgur {...item} />
                    </EmbedContainer>
                  );

                case 'instagram':
                  return (
                    <EmbedContainer key={`note-instagram-${item.id}`}>
                      <Instagram {...item} />
                    </EmbedContainer>
                  );

                case 'magnifiedGraphics':
                  return <MagnifiedGraphics key={`note-magnified-graphics-${idx}`} {...item} />;

                case 'onnetworkVideo':
                  return <OnnVideo key={`note-onnetwork-video-${item.id}`} {...item} />;

                case 'outerGifImage':
                  return <OuterGifImage key={`note-outer-gif-image-${item.id}`} {...item} />;

                case 'spotify':
                  return (
                    <EmbedContainer isAutoExpand key={`note-spotify-${item.id}`}>
                      <Spotify {...item} />
                    </EmbedContainer>
                  );

                case 'telegram':
                  return (
                    <EmbedContainer key={`note-telegram-${item.id}`}>
                      <Telegram {...item} />
                    </EmbedContainer>
                  );

                case 'tiktok':
                  return (
                    <EmbedContainer key={`note-tiktok-${item.id}`}>
                      <TikTok {...item} />
                    </EmbedContainer>
                  );

                case 'twitter':
                  return (
                    <EmbedContainer key={`note-twitter-${item.id}`}>
                      <Twitter {...item} />
                    </EmbedContainer>
                  );

                case 'uomArticle':
                case 'uomArticleRelated':
                  return embedArticles ? <UomArticle {...item} embedArticle={embedArticles[item.articleId]} key={`note-uomArticle-${item.articleId}`} /> : null;

                case 'windy':
                  return (
                    <EmbedContainer key={`note-windy-${item.src}`}>
                      <Windy {...item} />
                    </EmbedContainer>
                  );

                case 'youtube':
                  return (
                    <EmbedContainer key={`note-youtube-${item.id}`}>
                      <Youtube {...item} />
                    </EmbedContainer>
                  );

                default:
                  return <React.Fragment key={`note-embed-${idx}`} />;
              }
            });

          case 'interviewQuestion':
            return (
              <h4
                data-e='note-interviewQuestion'
                key={`note-interviewQuestion-${noteId}`}
              >
                {parseToHTML(element.items)}
              </h4>
            );

          case 'orderedList':
            return (
              <ol key={`notes-ordered-list-item-${noteId}`}>
                {makeList(`notes-ordered-list-item-${noteId}`, element.positions)}
              </ol>
            );

          case 'paragraph': {
            const isParagraphContainImage = element.items.find(item => item.type === 'uomImage');
            if (isParagraphContainImage) {
              return (
                <React.Fragment key={`note-uomImage-${idxC}`}>
                  {element.items.map((item, idx) => item.type === 'uomImage' ? <UomImage {...item} key={`uom-image-${item.imageId}-${idx}`} /> : null)}
                </React.Fragment>
              );
            }

            return (
              <p key={`note-paragraph-${idxC}`}>
                {element.items.map((item, idx) => paragraphContent(item, idx))}
              </p>
            );
          }

          case 'subTitle':
            return (
              <h4
                data-e='note-subTitle'
                key={`note-subTitle-${noteId}`}
              >
                {parseToHTML(element.items)}
              </h4>
            );

          case 'unorderedList':
            return (
              <ul key={`notes-unordered-list-item-${noteId}`}>
                {makeList(`notes-list-item-${noteId}`, element.positions)}
              </ul>
            );

          default:
            return <React.Fragment key={`note-default-${idxC}`} />;
        }
      })}
    </>
  );
}

const parseToHTML = (items: INoteItemText[]) => {
  return items.map(item => parse(item.text));
};

const makeList = (key: string, positions: INoteItemText[][]) => {
  return positions.map((item, idx) => <li key={`${key}-${idx}`}>{parseToHTML(item)}</li>);
};
